'use client'

import type { KanbanTask } from '@/components/blocks/kanban'
import type { DrawerKind } from '@/components/templates/drawers'

import { useState } from 'react'
import { ActionsIcon } from '@/components/icons/icon-set'
import { AppShell, PageBody } from '@/components/blocks/app-layout'
import { INBOX_ORGANIZATIONS, KanbanBoard, KanbanEmptyBoard } from '@/components/blocks/kanban'
import { DrawerHost } from '@/components/templates/drawers'

/*
 * GoodUnited / Kanban templates. Figma: 32 / Templates / Kanban.
 * Inbox mirrors the source board; Empty is the starter for a new organization.
 * Opening a card (or Add action) opens the editor drawer. Organization rows expand and collapse.
 */
export type KanbanTemplateKind = 'inbox' | 'empty'

export const KANBAN_TEMPLATES: { kind: KanbanTemplateKind, title: string, description: string }[] = [
    { kind: 'inbox', title: 'Inbox', description: 'Five organizations, one expanded, with applied filters.' },
    { kind: 'empty', title: 'Empty board', description: 'The starter for a new organization.' }
]

export function KanbanTemplate({ kind = 'inbox' }: { kind?: KanbanTemplateKind }) {
    const [drawer, setDrawer] = useState<DrawerKind | null>(null)
    const [editor, setEditor] = useState({ title: 'New action', description: 'Draft · not yet assigned' })

    const openTask = (task: KanbanTask) => {
        const organization = INBOX_ORGANIZATIONS.find(item => item.tasks.some(entry => entry.id === task.id))
        setEditor({ title: task.title, description: `Action · ${organization?.name ?? 'Organization'}` })
        setDrawer('editor')
    }

    const addAction = () => {
        setEditor({ title: 'New action', description: 'Draft · not yet assigned' })
        setDrawer('editor')
    }

    return (
        <AppShell title='Inbox' icon={<ActionsIcon />} activeItem='actions' showDate>
            <PageBody title='Inbox' description='AI-recommended actions, reviewed and approved by your GoodUnited team'>
                {kind === 'inbox'
                    ? <KanbanBoard onSelectTask={openTask} onAddAction={addAction} />
                    : <KanbanEmptyBoard onAddAction={addAction} />}
            </PageBody>
            <DrawerHost
                key={editor.title}
                drawer={drawer}
                onDrawerChange={setDrawer}
                editorTitle={editor.title}
                editorDescription={editor.description}
            />
        </AppShell>
    )
}
